import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import WorkbookVideoPlayer from './WorkbookVideoPlayer';
import { checkAndUnlockPostAssessment, WorkbookCompletionModal } from '../utils/checkAndUnlockPostAssessment';

const AI_SCORING_PROMPT = `You are an expert AI literacy coach evaluating a learner's RICE prompt.

Score the prompt below on each of the four RICE components using this rubric:
1 = Missing, 2 = Partial, 3 = Solid, 4 = Excellent

ROLE: Is there a specific professional role with enough context to shape the output? (1-4)
INTENT: Is the specific deliverable named clearly, not just a topic? (1-4)
CONTEXT: Does it include audience, stakes, background, and/or tone? (1-4)
EXTRAS: Are format, length, constraints, or flags specified? (1-4)

After scoring each component:
1. Give an overall quality score out of 16.
2. Name the single most important thing that would make this prompt significantly better.
3. Rewrite the prompt incorporating that improvement.
4. Explain in one sentence what changed and why it matters.

Here's the prompt to evaluate:
[PASTE YOUR RICE PROMPT HERE]`;

const RUBRIC = [
  {
    criterion: 'ROLE',
    missing: 'No role given: generic AI assistant',
    partial: 'Job title only, no context',
    solid: 'Specific professional role with expertise level',
    excellent: 'Role + organizational context that shapes the register',
  },
  {
    criterion: 'INTENT',
    missing: 'No clear output requested',
    partial: 'Topic given but not the deliverable',
    solid: 'Specific output type named clearly',
    excellent: 'Output type + required elements specified',
  },
  {
    criterion: 'CONTEXT',
    missing: 'No context provided',
    partial: 'One context element (e.g. tone only)',
    solid: 'Audience and stakes included',
    excellent: 'Audience, stakes, background, and tone all addressed',
  },
  {
    criterion: 'EXTRAS',
    missing: 'No format or constraints',
    partial: 'One constraint (e.g. length only)',
    solid: 'Format and length specified',
    excellent: 'Format, length, tone, inclusions, and flags specified',
  },
  {
    criterion: 'OVERALL QUALITY',
    missing: 'Would need complete rewrite',
    partial: 'Needs significant editing',
    solid: 'Usable with minor edits',
    excellent: 'Ready to use with 2-minute review',
  },
];

function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, sublabel, value, onChange, minHeight = 60, placeholder = '' }) {
  return (
    <div style={s.field}>
      {label && <div style={s.fieldLabel}>{label}</div>}
      {sublabel && <div style={s.fieldSublabel}>{sublabel}</div>}
      <textarea
        style={{ ...s.textarea, minHeight }}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function ScoreField({ label, value, onChange }) {
  return (
    <div style={s.scoreField}>
      <div style={s.scoreLabel}>{label}</div>
      <select style={s.scoreSelect} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
      </select>
      <div style={s.scoreOf}>/ 4</div>
    </div>
  );
}

function CalloutBox({ title, children }) {
  return (
    <div style={s.callout}>
      <div style={s.calloutTitle}>{title}</div>
      <div style={s.calloutBody}>{children}</div>
    </div>
  );
}

export default function WorkbookDay1({ user, onNav, onMarkComplete }) {
  const [data, setData] = useState({});
  const [assessmentData, setAssessmentData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPostUnlockModal, setShowPostUnlockModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [wbSnap, assessSnap] = await Promise.all([
        getDoc(doc(db, 'workbooks', user.uid)),
        getDoc(doc(db, 'assessments', user.uid)),
      ]);
      if (wbSnap.exists()) {
        const prog = wbSnap.data().progress || {};
        const day1 = prog.day1 || {};
        setData(day1.fields || {});
        setIsComplete(day1.status === 'complete');
      }
      if (assessSnap.exists()) setAssessmentData(assessSnap.data());
      setLoading(false);
    };
    load();
  }, [user.uid]);

  const set = (key, val) => setData(prev => ({ ...prev, [key]: val }));

  const calcTotal = (prefix) => {
    const r = parseInt(data[`${prefix}_r`]) || 0;
    const i = parseInt(data[`${prefix}_i`]) || 0;
    const c = parseInt(data[`${prefix}_c`]) || 0;
    const e = parseInt(data[`${prefix}_e`]) || 0;
    return r + i + c + e;
  };

  const handleSave = async () => {
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    const existingProgress = existing.progress || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...existingProgress,
        day1: {
          ...existingProgress.day1,
          fields: data,
          status: isComplete ? 'complete' : 'inprog',
          updatedAt: serverTimestamp(),
        }
      }
    }, { merge: true });
    setSaving(false);
    setLastSaved(new Date());
  };

  const handleMarkComplete = async (confirmed) => {
    setShowCompleteModal(false);
    if (!confirmed) return;
    setIsComplete(true);
    setSaving(true);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      userId: user.uid,
      progress: {
        ...(existing.progress || {}),
        day1: {
          fields: data,
          status: 'complete',
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      }
    }, { merge: true });
    setSaving(false);
    // Check if all assigned workbooks are now complete, unlock post-assessment if so
    const unlockResult = await checkAndUnlockPostAssessment(user.uid);
    if (unlockResult.unlocked) {
      setShowPostUnlockModal(true);
    }
  };

  const handleUnmarkComplete = async () => {
    setIsComplete(false);
    const wbRef = doc(db, 'workbooks', user.uid);
    const existing = (await getDoc(wbRef)).data() || {};
    await setDoc(wbRef, {
      ...existing,
      progress: {
        ...(existing.progress || {}),
        day1: { ...(existing.progress?.day1 || {}), status: 'inprog', updatedAt: serverTimestamp() }
      }
    }, { merge: true });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(AI_SCORING_PROMPT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const pre = assessmentData?.pre?.scores;

  if (loading) return <div style={{ color: '#8A8A8A', fontSize: '14px', padding: '20px' }}>Loading Day 1...</div>;

  return (
    <div style={s.wrapper}>

      {/* FIXED TOP BAR */}
      <div style={s.topBar}>
        <span style={s.backLink} onClick={() => onNav('workbooks')}>← Back to Workbooks</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {lastSaved && <span style={s.savedNote}>Saved {lastSaved.toLocaleTimeString()}</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      <WorkbookVideoPlayer dayNumber={1} user={user} />

      <div style={s.header}>
        <div style={s.dayTag}>Day 1</div>
        <div style={s.headerTitle}>Foundation and Prompting Fundamentals</div>
        <div style={s.headerDesc}>Work through each section during or after your session. Click Save at any time. Your responses are private.</div>
        {isComplete && <div style={s.completeBadge}>✓ Marked Complete</div>}
      </div>

      {/* BASELINE SNAPSHOT */}
      {pre && (
        <div style={s.snapshotBox}>
          <div style={s.snapshotTitle}>AI Readiness Snapshot — Your Baseline Results</div>
          <div style={s.snapshotDesc}>Complete the AI Readiness Snapshot before Day 1 begins. These scores were auto-populated from your assessment. You'll compare them at Day 10.</div>
          <div style={s.snapshotGrid}>
            {[
              { label: 'Frequency', val: pre.freq },
              { label: 'Confidence', val: pre.conf },
              { label: 'Use Case Range', val: pre.range },
              { label: 'Advocacy', val: pre.adv },
            ].map(d => (
              <div key={d.label} style={s.snapshotCard}>
                <div style={s.snapshotLabel}>{d.label}</div>
                <div style={s.snapshotVal}>{d.val}<span style={{ fontSize: '13px', color: '#8A8A8A', fontWeight: '400' }}> / 5</span></div>
              </div>
            ))}
          </div>
          <div style={s.snapshotTotal}>Overall Level: <strong>{assessmentData?.pre?.level}</strong> &nbsp;&nbsp; Total Score: <strong>{pre.total} / 20</strong></div>
          <Field label="What surprised you about your results?" value={data.snapshot_surprise} onChange={v => set('snapshot_surprise', v)} minHeight={60} />
          <div style={s.snapshotNote}>Keep this page. You'll fill in your Day 10 scores in the same table and see exactly how far you moved.</div>
        </div>
      )}

      {/* KEY CONCEPT */}
      <div style={s.keyConceptBox}>
        <div style={s.keyConceptLabel}>Key Concept Reminder</div>
        <div style={s.keyConceptText}>AI isn't thinking or reasoning like a person, and it isn't a reliable fact checker. It predicts the most useful response based on patterns learned from enormous amounts of text, and modern tools can also pull from the web, your files, and memory features. Your input still determines everything. A vague prompt gets a vague answer. RICE fixes that. Role tells the AI who to be. Intent tells it what to make. Context gives it what it can't guess. Extras shape the output with precision. Every good prompt has all four. Every mediocre prompt is missing at least one. The question that travels through all 10 days: is this a workflow I would run every single day? If yes, that's significant use. That's what you're building toward.</div>
      </div>

      {/* IN-SESSION EXERCISE */}
      <Section title="In-Session Exercise — Build Your First RICE Prompt">
        <div style={s.exerciseIntro}>Pick one real task from your actual work, not a practice scenario. Something significant: a task you would run every single day. Work through each component below, then write the complete prompt and run it.</div>

        <div style={s.riceTable}>
          {[
            { key: 'R', label: 'R — ROLE', hint: 'Who should the AI be? Not just a title: a specific professional with specific expertise.', example: 'Example: You are a senior HR communications specialist at a mid-size company.' },
            { key: 'I', label: 'I — INTENT', hint: 'What exactly do you want it to produce? Name the deliverable, not the topic.', example: 'Example: Write a 200-word welcome email for a new hire joining the marketing team.' },
            { key: 'C', label: 'C — CONTEXT', hint: "What does it need to know that it can't guess? Audience, stakes, background, tone.", example: 'Example: Starting Monday, joining a fast-paced team, first corporate job, manager is Sam.' },
            { key: 'E', label: 'E — EXTRAS', hint: 'Format, length, tone, what to include, what to leave out, what to flag.', example: 'Example: Warm but professional. Include first-day logistics. Under 200 words.' },
          ].map(row => (
            <div key={row.key} style={s.riceRow}>
              <div style={s.riceLabel}>{row.label}</div>
              <div style={s.riceRight}>
                <div style={s.riceHint}>{row.hint}</div>
                <div style={s.riceExample}>{row.example}</div>
                <div style={s.riceAnswerLabel}>Your answer:</div>
                <textarea style={s.riceInput} value={data[`rice_${row.key}`] || ''} onChange={e => set(`rice_${row.key}`, e.target.value)} />
              </div>
            </div>
          ))}
        </div>

        <Field label="Your Complete RICE Prompt" sublabel="Write your full prompt here before running it:" value={data.rice_full_prompt} onChange={v => set('rice_full_prompt', v)} minHeight={100} />
        <Field label="What came back" sublabel="Brief summary of the output:" value={data.rice_result} onChange={v => set('rice_result', v)} minHeight={60} />
        <Field label="What would you change about the prompt?" value={data.rice_change} onChange={v => set('rice_change', v)} minHeight={60} />
      </Section>

      {/* SCORING CRITERIA */}
      <Section title="Scoring Criteria — Know What Good Looks Like">
        <div style={s.rubricIntro}>Review these criteria before you build your prompt. They tell you exactly what a strong RICE prompt looks like.</div>
        <div style={s.rubricTable}>
          <div style={s.rubricHeader}>
            <div style={{ ...s.rubricCol0, color: '#FFFFFF', fontWeight: '700' }}>Criterion</div>
            <div style={{ ...s.rubricCol, color: '#FFFFFF', fontWeight: '700' }}>1 — Missing</div>
            <div style={{ ...s.rubricCol, color: '#FFFFFF', fontWeight: '700' }}>2 — Partial</div>
            <div style={{ ...s.rubricCol, color: '#FFFFFF', fontWeight: '700' }}>3 — Solid</div>
            <div style={{ ...s.rubricCol, color: '#FFFFFF', fontWeight: '700' }}>4 — Excellent</div>
          </div>
          {RUBRIC.map(row => (
            <div key={row.criterion} style={s.rubricRow}>
              <div style={{ ...s.rubricCol0, fontWeight: '600', color: '#1B2A4A' }}>{row.criterion}</div>
              <div style={s.rubricCol}>{row.missing}</div>
              <div style={s.rubricCol}>{row.partial}</div>
              <div style={s.rubricCol}>{row.solid}</div>
              <div style={{ ...s.rubricCol, color: '#1B2A4A', fontWeight: '500' }}>{row.excellent}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* AI SCORING PROMPT */}
      <div style={s.aiBox}>
        <div style={s.aiBoxTitle}>AI Scoring Prompt — Evaluate Your Own Work</div>
        <div style={s.aiBoxSub}>Paste this into any AI tool along with your RICE prompt. Read the feedback carefully and note what you would improve.</div>
        <div style={s.aiBoxText}>{AI_SCORING_PROMPT}</div>
        <button style={s.copyBtn} onClick={handleCopy}>{copied ? 'Copied!' : 'Copy Prompt'}</button>
      </div>

      {/* SCORE TRACKING */}
      <Section title="Record Your AI Score">
        <div style={s.scoreRow}>
          {['R', 'I', 'C', 'E'].map(k => (
            <ScoreField key={k} label={k} value={data[`score1_${k.toLowerCase()}`]} onChange={v => set(`score1_${k.toLowerCase()}`, v)} />
          ))}
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{calcTotal('score1')} / 16</div>
          </div>
        </div>
        <Field label="Most important improvement the AI identified:" value={data.score1_improvement} onChange={v => set('score1_improvement', v)} minHeight={60} />
        <Field label="Did the AI's rewritten version surprise you? What did it add or change?" value={data.score1_surprise} onChange={v => set('score1_surprise', v)} minHeight={60} />
      </Section>

      {/* THE STANDARD */}
      <CalloutBox title="THE STANDARD: 16 / 16">
        <div>Keep trying to get 2 prompts that score 16/16. Use the revision workspace below. Run the AI scoring prompt again with your revised version each time. Keep going until you hit the standard, then do it again with a second prompt.</div>
        <div style={{ marginTop: '10px', fontStyle: 'italic', opacity: 0.85 }}>This is your scaffolding, not your ceiling. As you get fluent, your prompts will outgrow the rubric.</div>
      </CalloutBox>

      {/* REVISION WORKSPACE */}
      <Section title="Revision Workspace — Keep Going Until You Hit 16/16">
        <div style={s.revisionLabel}>Revision 1</div>
        <Field label="What I changed and why:" value={data.rev1_why} onChange={v => set('rev1_why', v)} minHeight={60} />
        <Field label="Revised prompt:" value={data.rev1_prompt} onChange={v => set('rev1_prompt', v)} minHeight={80} />
        <div style={s.scoreRow}>
          {['R', 'I', 'C', 'E'].map(k => (
            <ScoreField key={k} label={k} value={data[`rev1_${k.toLowerCase()}`]} onChange={v => set(`rev1_${k.toLowerCase()}`, v)} />
          ))}
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{calcTotal('rev1')} / 16</div>
          </div>
        </div>

        <div style={{ ...s.revisionLabel, marginTop: '20px' }}>Revision 2 (if needed)</div>
        <Field label="What I changed and why:" value={data.rev2_why} onChange={v => set('rev2_why', v)} minHeight={60} />
        <Field label="Revised prompt:" value={data.rev2_prompt} onChange={v => set('rev2_prompt', v)} minHeight={80} />
        <div style={s.scoreRow}>
          {['R', 'I', 'C', 'E'].map(k => (
            <ScoreField key={k} label={k} value={data[`rev2_${k.toLowerCase()}`]} onChange={v => set(`rev2_${k.toLowerCase()}`, v)} />
          ))}
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Total</div>
            <div style={s.scoreTotalVal}>{calcTotal('rev2')} / 16</div>
          </div>
        </div>
      </Section>

      {/* SECOND PROMPT */}
      <CalloutBox title="NOW DO IT AGAIN — SECOND PROMPT">
        Pick a different real task. Build a second RICE prompt from scratch. Run the AI scoring prompt. Revise until it hits 16/16. Aim for two prompts at the standard before moving to the reflection.
      </CalloutBox>

      <Section title="Second Prompt">
        <Field label="Second prompt — Task:" value={data.p2_task} onChange={v => set('p2_task', v)} minHeight={50} />
        <Field label="Your RICE Prompt:" value={data.p2_prompt} onChange={v => set('p2_prompt', v)} minHeight={100} />
        <div style={s.scoreRow}>
          {['R', 'I', 'C', 'E'].map(k => (
            <ScoreField key={k} label={k} value={data[`p2_first_${k.toLowerCase()}`]} onChange={v => set(`p2_first_${k.toLowerCase()}`, v)} />
          ))}
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>First run</div>
            <div style={s.scoreTotalVal}>{calcTotal('p2_first')} / 16</div>
          </div>
        </div>
        <Field label="What I changed to hit 16:" value={data.p2_change} onChange={v => set('p2_change', v)} minHeight={60} />
        <div style={s.scoreRow}>
          {['R', 'I', 'C', 'E'].map(k => (
            <ScoreField key={k} label={k} value={data[`p2_final_${k.toLowerCase()}`]} onChange={v => set(`p2_final_${k.toLowerCase()}`, v)} />
          ))}
          <div style={s.scoreTotalBox}>
            <div style={s.scoreTotalLabel}>Final score</div>
            <div style={s.scoreTotalVal}>{calcTotal('p2_final')} / 16</div>
          </div>
        </div>
      </Section>

      {/* REFLECTION */}
      <Section title="Reflection">
        <Field
          label="1. When you think about your actual job, what's one recurring task that genuinely wastes more time than it should? Is it a candidate for significant AI use, something you would run every single day?"
          value={data.reflection_1} onChange={v => set('reflection_1', v)} minHeight={80}
        />
        <Field
          label="2. Before today, what was your biggest misconception about what AI can do? Did anything in this session challenge or confirm it?"
          value={data.reflection_2} onChange={v => set('reflection_2', v)} minHeight={80}
        />
      </Section>

      {/* WINS TRACKER */}
      <Section title="Wins Tracker — Day 1">
        <div style={s.winsTable}>
          <div style={s.winsHeader}>
            <div style={s.winsCol}>What I Tried</div>
            <div style={s.winsCol}>Tool Used</div>
            <div style={s.winsCol}>What Changed</div>
            <div style={s.winsCol}>Time Saved</div>
          </div>
          {[1, 2, 3].map(n => (
            <div key={n} style={s.winsRow}>
              {['tried', 'tool', 'changed', 'saved'].map(f => (
                <div key={f} style={s.winsCell}>
                  <textarea style={s.winsInput} value={data[`wins_${n}_${f}`] || ''} onChange={e => set(`wins_${n}_${f}`, e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      {/* BEFORE DAY 2 */}
      <CalloutBox title="BEFORE DAY 2 — YOUR ASSIGNMENT">
        Run a RICE prompt on a second real task, different from what you built today. Something from a different part of your work. Then:
        <ol style={{ paddingLeft: '18px', marginTop: '8px', lineHeight: '1.8' }}>
          <li>Write down the full prompt you ran.</li>
          <li>Note what came back: was it useful, generic, surprising, or off?</li>
          <li>Note what you would change about the prompt.</li>
          <li>Do the math: how long would this task have taken without AI? How long did it actually take? Write both numbers.</li>
          <li>Bring all of this to Day 2. We're using it.</li>
        </ol>
      </CalloutBox>
      <Field label="Write your second task here so you don't forget it:" value={data.assignment_task} onChange={v => set('assignment_task', v)} minHeight={60} />

      {/* PROMPT LIBRARY */}
      <Section title="My Prompt Library">
        <div style={s.libraryIntro}>Your Prompt Library is a working tool, not a folder of things you save and forget. Every significant prompt you build goes here with all six fields complete. The output note and chain prompt fields are the ones that make this useful to anyone, including future you.</div>
        {[1, 2, 3].map(n => (
          <div key={n} style={s.libraryEntry}>
            <div style={s.libraryEntryTitle}>Entry {n}</div>
            {[
              { key: 'usecase', label: '01 USE CASE', hint: 'What specific task does this prompt handle?' },
              { key: 'workflow', label: '02 WORKFLOW', hint: 'Which larger workflow does this step belong to?' },
              { key: 'roletag', label: '03 ROLE TAG', hint: 'Your role with enough context to shape the output.' },
              { key: 'prompt', label: '04 THE PROMPT', hint: 'Full prompt text. Every bracket clearly marked.' },
              { key: 'output', label: '05 OUTPUT NOTE', hint: 'One sentence: what does AI produce and where does it go?' },
              { key: 'chain', label: '06 CHAIN PROMPT', hint: 'The next logical step you would take with that output.' },
            ].map(field => (
              <div key={field.key} style={s.libraryRow}>
                <div style={s.libraryFieldLabel}>
                  <div style={s.libraryFieldName}>{field.label}</div>
                  <div style={s.libraryFieldHint}>{field.hint}</div>
                </div>
                <textarea
                  style={{ ...s.textarea, minHeight: field.key === 'prompt' ? 80 : 50, flex: 1 }}
                  value={data[`lib_${n}_${field.key}`] || ''}
                  onChange={e => set(`lib_${n}_${field.key}`, e.target.value)}
                />
              </div>
            ))}
          </div>
        ))}
      </Section>

      {/* SAVE AND COMPLETE */}
      <div style={s.bottomBar}>
        <div style={s.saveRow}>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
          {lastSaved && <span style={s.savedNote}>Last saved {lastSaved.toLocaleTimeString()}</span>}
        </div>

        {isComplete ? (
          <div style={s.completeRow}>
            <div style={s.completedBadge}>✓ Day 1 Complete</div>
            <button style={s.unmarkBtn} onClick={handleUnmarkComplete}>Unmark as Complete</button>
          </div>
        ) : (
          <button style={s.completeBtn} onClick={() => setShowCompleteModal(true)}>
            Mark Day 1 as Complete
          </button>
        )}
      </div>

      {/* CONFIRMATION MODAL */}
      {showCompleteModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark Day 1 as Complete?</div>
            <div style={s.modalText}>This will count toward your certificate progress. You can unmark it at any time if you need to come back and finish something.</div>
            <div style={s.modalBtns}>
              <button style={s.modalCancel} onClick={() => setShowCompleteModal(false)}>Cancel</button>
              <button style={s.modalConfirm} onClick={() => handleMarkComplete(true)}>Yes, Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      <WorkbookCompletionModal
        isOpen={showPostUnlockModal}
        onClose={() => setShowPostUnlockModal(false)}
        onGoToAssessment={() => { setShowPostUnlockModal(false); onNav('assessment'); }}
        onGoToExam={() => { setShowPostUnlockModal(false); onNav('exam'); }}
      />
    </div>
  );
}

const s = {
  wrapper: { maxWidth: '720px', paddingBottom: '60px' },
  topBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '12px 20px', marginBottom: '16px', position: 'sticky', top: '52px', zIndex: 50 },
  backLink: { fontSize: '12px', color: '#1B2A4A', cursor: 'pointer', fontWeight: '500' },
  header: { background: '#1B2A4A', padding: '24px 28px', marginBottom: '20px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  headerTitle: { fontFamily: 'Playfair Display, serif', fontSize: '22px', color: '#fff', marginBottom: '6px' },
  headerDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '300' },
  completeBadge: { marginTop: '10px', display: 'inline-block', background: '#1a8a4a', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px', letterSpacing: '0.5px' },
  snapshotBox: { background: '#FAF8F5', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  snapshotTitle: { fontFamily: 'Playfair Display, serif', fontSize: '17px', color: '#1B2A4A', marginBottom: '4px' },
  snapshotDesc: { fontSize: '12px', color: '#3D3D3D', fontWeight: '400', marginBottom: '16px', lineHeight: '1.6' },
  snapshotGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '12px' },
  snapshotCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '14px', textAlign: 'center' },
  snapshotLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#3D3D3D', marginBottom: '4px' },
  snapshotVal: { fontFamily: 'Playfair Display, serif', fontSize: '28px', fontWeight: '900', color: '#1B2A4A' },
  snapshotTotal: { fontSize: '13px', color: '#3D3D3D', marginBottom: '16px' },
  snapshotNote: { fontSize: '12px', color: '#3D3D3D', fontStyle: 'italic', marginTop: '8px' },
  keyConceptBox: { background: '#F2F0EC', borderLeft: '3px solid #D4A847', padding: '16px 20px', marginBottom: '20px' },
  keyConceptLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  keyConceptText: { fontSize: '13px', color: '#3D3D3D', fontWeight: '400', lineHeight: '1.8' },
  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '16px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', marginBottom: '16px', paddingBottom: '10px', borderBottom: '1px solid #E8E6E2' },
  exerciseIntro: { fontSize: '13px', color: '#3D3D3D', fontWeight: '400', lineHeight: '1.7', marginBottom: '20px' },
  riceTable: { border: '1.5px solid #E8E6E2', marginBottom: '20px' },
  riceRow: { display: 'flex', borderBottom: '1px solid #E8E6E2' },
  riceLabel: { width: '120px', flexShrink: 0, background: '#F2F0EC', padding: '14px 12px', fontSize: '12px', fontWeight: '700', color: '#1B2A4A', borderRight: '1px solid #E8E6E2', display: 'flex', alignItems: 'flex-start' },
  riceRight: { flex: 1, padding: '14px' },
  riceHint: { fontSize: '12px', color: '#3D3D3D', marginBottom: '4px', lineHeight: '1.5' },
  riceExample: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', marginBottom: '10px', lineHeight: '1.5' },
  riceAnswerLabel: { fontSize: '11px', fontWeight: '600', color: '#1B2A4A', marginBottom: '4px', letterSpacing: '0.5px' },
  riceInput: { width: '100%', border: '1.5px solid #E8E6E2', padding: '8px 10px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '55px' },
  field: { marginBottom: '16px' },
  fieldLabel: { fontSize: '13px', fontWeight: '500', color: '#1B2A4A', marginBottom: '4px', lineHeight: '1.5' },
  fieldSublabel: { fontSize: '12px', color: '#3D3D3D', marginBottom: '6px', fontWeight: '400' },
  textarea: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', lineHeight: '1.6', fontWeight: '400' },
  rubricIntro: { fontSize: '13px', color: '#3D3D3D', marginBottom: '12px', fontWeight: '400' },
  rubricTable: { border: '1.5px solid #E8E6E2', fontSize: '12px', marginBottom: '8px' },
  rubricHeader: { display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 1fr', background: '#1B2A4A', color: '#FFFFFF', fontWeight: '700', fontSize: '11px', letterSpacing: '0.5px' },
  rubricRow: { display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E6E2' },
  rubricCol0: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', fontSize: '11px' },
  rubricCol: { padding: '10px 12px', borderRight: '1px solid #E8E6E2', color: '#3D3D3D', lineHeight: '1.5', fontSize: '11px' },
  aiBox: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  aiBoxTitle: { fontSize: '13px', fontWeight: '600', color: '#D4A847', marginBottom: '6px' },
  aiBoxSub: { fontSize: '12px', color: 'rgba(255,255,255,0.8)', fontWeight: '300', marginBottom: '12px' },
  aiBoxText: { fontSize: '12px', color: '#FFFFFF', lineHeight: '1.8', marginBottom: '12px', fontWeight: '300', whiteSpace: 'pre-line' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '8px 18px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  scoreRow: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' },
  scoreField: { display: 'flex', alignItems: 'center', gap: '6px' },
  scoreLabel: { fontSize: '13px', fontWeight: '700', color: '#1B2A4A', width: '20px' },
  scoreSelect: { border: '1.5px solid #E8E6E2', padding: '6px 8px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', background: '#FAF8F5', outline: 'none', width: '56px' },
  scoreOf: { fontSize: '12px', color: '#3D3D3D' },
  scoreTotalBox: { marginLeft: 'auto', background: '#1B2A4A', padding: '8px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  scoreTotalLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' },
  scoreTotalVal: { fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '900', color: '#D4A847' },
  callout: { background: '#1B2A4A', padding: '20px 24px', marginBottom: '16px' },
  calloutTitle: { fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px' },
  calloutBody: { fontSize: '13px', color: '#FFFFFF', fontWeight: '300', lineHeight: '1.7' },
  revisionLabel: { fontSize: '12px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '12px' },
  winsTable: { border: '1.5px solid #E8E6E2' },
  winsHeader: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', background: '#1B2A4A' },
  winsCol: { padding: '10px 12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#fff', borderRight: '1px solid rgba(255,255,255,0.1)' },
  winsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', borderBottom: '1px solid #E8E6E2' },
  winsCell: { borderRight: '1px solid #E8E6E2', padding: '4px' },
  winsInput: { width: '100%', border: 'none', padding: '8px', fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#3D3D3D', background: '#FAF8F5', outline: 'none', resize: 'vertical', minHeight: '50px' },
  libraryIntro: { fontSize: '13px', color: '#3D3D3D', fontWeight: '400', lineHeight: '1.7', marginBottom: '20px' },
  libraryEntry: { border: '1.5px solid #E8E6E2', marginBottom: '16px' },
  libraryEntryTitle: { background: '#1B2A4A', color: '#D4A847', padding: '8px 16px', fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' },
  libraryRow: { display: 'flex', borderBottom: '1px solid #E8E6E2' },
  libraryFieldLabel: { width: '140px', flexShrink: 0, background: '#F2F0EC', padding: '12px', borderRight: '1px solid #E8E6E2' },
  libraryFieldName: { fontSize: '11px', fontWeight: '700', color: '#1B2A4A', marginBottom: '4px' },
  libraryFieldHint: { fontSize: '11px', color: '#3D3D3D', fontStyle: 'italic', lineHeight: '1.5' },
  bottomBar: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px', marginBottom: '16px' },
  saveRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  saveBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  savedNote: { fontSize: '11px', color: '#1a8a4a', fontWeight: '500' },
  completeRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  completedBadge: { background: '#eaf3de', color: '#3b6d11', padding: '8px 16px', fontSize: '12px', fontWeight: '700' },
  unmarkBtn: { fontSize: '12px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  completeBtn: { background: '#C0392B', color: '#fff', border: 'none', padding: '12px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer', letterSpacing: '0.5px' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(27,42,74,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  modal: { background: '#FAF8F5', padding: '36px 40px', maxWidth: '420px', width: '90%' },
  modalTitle: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  modalText: { fontSize: '13px', color: '#3D3D3D', fontWeight: '400', lineHeight: '1.7', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px' },
  modalCancel: { background: 'transparent', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  modalConfirm: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 24px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
